import { useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import LocationAutocompleteInput from "@components/fields/LocationAutocompleteInput";
import Select from "@components/fields/Select";
import Form from "@components/forms/Form";
import HandleQuery from "@components/HandleQuery";
import {
  CareProfileAgeEnum,
  GetClientWithLocationDocument,
  GetClientWithLocationQuery,
  GetClientWithLocationQueryVariables,
  JourneyCreateDocument,
  JourneyCreateMutation,
  JourneyCreateMutationVariables
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { optionsForAge } from "@utils/optionUtils";

type Journey = NonNullable<NonNullable<JourneyCreateMutation["journeyCreate"]>["journey"]>;

type FormData = {
  age: CareProfileAgeEnum | null;
  careProfilePlaceId: string | null;
  clientPlaceId: string | null;
  name: string | null;
};

type Props = {
  autofocus: boolean;
  clientId: string;
  onCancel: () => void;
  onError: () => void;
  onSuccess: (journey: Journey) => void;
};

const NewJourneyForm = ({ autofocus, clientId, onCancel, onError, onSuccess }: Props) => {
  const getClientQuery = useQuery<GetClientWithLocationQuery, GetClientWithLocationQueryVariables>(
    GetClientWithLocationDocument,
    {
      variables: {
        id: clientId
      }
    }
  );

  const defaultValues: FormData = {
    age: null,
    careProfilePlaceId: null,
    clientPlaceId: null,
    name: ""
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [journeyCreate] = useMutation<JourneyCreateMutation, JourneyCreateMutationVariables>(JourneyCreateDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      age: yup
        .string()
        .oneOf(Object.values(CareProfileAgeEnum))
        .required(intl.formatMessage({ id: "errors.newJourney.age.blank" })),
      careProfilePlaceId: yup
        .string()
        .defined()
        .when(["age"], ([age], schema) => {
          if (age === CareProfileAgeEnum.deceased) {
            return schema.nullable();
          } else {
            return schema.required(intl.formatMessage({ id: "errors.newJourney.placeId.blank" }));
          }
        }),
      clientPlaceId: yup.lazy(() => {
        if (getClientQuery.data?.client.location.placeId) {
          return yup.string().defined().nullable();
        }
        return yup.string().required(intl.formatMessage({ id: "errors.newJourney.placeId.blank" }));
      }),
      name: yup.string().required(intl.formatMessage({ id: "errors.newJourney.name.blank" }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const age = form.watch("age");

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return journeyCreate({
      variables: {
        age: form.getValues().age as CareProfileAgeEnum,
        careProfilePlaceId: form.getValues().careProfilePlaceId as string,
        clientId,
        clientPlaceId: getClientQuery.data?.client.location.placeId ? undefined : form.getValues().clientPlaceId,
        name: form.getValues().name as string
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.journeyCreate,
        intl,
        onError,
        onSuccess: () => {
          if (response.data?.journeyCreate?.journey) {
            onSuccess(response.data.journeyCreate.journey);
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.journey"
      });
    });
  }, [journeyCreate, form, clientId, getClientQuery.data?.client.location.placeId, intl, onError, onSuccess]);

  const ageOptions = optionsForAge(intl);

  return (
    <HandleQuery query={getClientQuery}>
      {data => (
        <Form data-test="new-journey-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
          <BaseErrors errors={baseErrors} />
          <Input<FormData>
            aria-required="true"
            autofocus={autofocus}
            control={form.control}
            data-test="name-input"
            error={form.formState.errors.name}
            label={intl.formatMessage({ id: "forms.careProfile.nameLabel" })}
            name="name"
            required
          />
          <Select<FormData>
            control={form.control}
            error={form.formState.errors.age}
            label={intl.formatMessage({ id: "forms.careProfile.ageLabel" })}
            name="age"
            options={ageOptions}
            required
          />
          <div className={age === CareProfileAgeEnum.deceased ? "ion-hide" : ""}>
            <LocationAutocompleteInput
              control={form.control}
              error={form.formState.errors.careProfilePlaceId}
              label={intl.formatMessage({ id: "forms.careProfile.locationLabel" })}
              location={{ address: null, placeId: null }}
              name="careProfilePlaceId"
              required
              subLabel={intl.formatMessage({ id: "forms.careProfile.locationSublabel" })}
            />
          </div>

          <div className={data.client.location.placeId ? "ion-hide" : ""}>
            <LocationAutocompleteInput
              control={form.control}
              error={form.formState.errors.clientPlaceId}
              label={intl.formatMessage({ id: "forms.careProfile.clientLocationLabel" })}
              location={{ address: null, placeId: null }}
              name="clientPlaceId"
              placeholder={intl.formatMessage({ id: "forms.careProfile.clientLocationPlaceholder" })}
              required
              subLabel={intl.formatMessage({ id: "forms.careProfile.locationSublabel" })}
            />
          </div>
          <FormButtons hookForm={form} onCancel={onCancel} />
        </Form>
      )}
    </HandleQuery>
  );
};
export default NewJourneyForm;
